import { Validator } from 'fluentvalidation-ts';
import { fieldRequiredValidationMessage } from '../../../../../validators/sharedValidators';
import {
  beAnInteger,
  beGreaterThanOrEqualTo,
  beValidPercentage,
  haveDecimalPlaces,
} from '../../../../../validators/stringValidators';
import { BuildTypeCode } from '../../../../projects/project';
import { UnitFormModel } from '../unitFormModel';
import {
  getDefaultUnitFloorElementFormModels,
  UnitFloorElementFormModel,
  UnitFloorElementFormModelValidator,
} from './unitFloorElementFormModel';
import {
  getDefaultUnitRoofElementFormModels,
  UnitRoofElementFormModel,
  UnitRoofElementFormModelValidator,
} from './unitRoofElementFormModel';
import {
  getDefaultUnitWallElementFormModel,
  UnitWallElementFormModel,
  UnitWallElementFormModelValidator,
} from './unitWallElementFormModel';

export type UnitHeatLossDetailsFormModel = {
  generalAllowanceFactorPercentage: string;
  exposureFactorPercentage: 0 | 10;
  propertyFootprintInMetresSquared: string;
  propertyExternalPerimeterInMetres: string;
  buildTypeCode: BuildTypeCode;
  externalAirTemperatureInDegreesCelsius: string;
  wallElements: Array<UnitWallElementFormModel>;
  roofElements: Array<UnitRoofElementFormModel>;
  floorElements: Array<UnitFloorElementFormModel>;
};

export const getInitialUnitHeatLossDetailsFormModel = (
  buildTypeCode: BuildTypeCode,
  externalAirTemperatureInDegreesCelsius: string,
  unit: UnitFormModel,
): UnitHeatLossDetailsFormModel => ({
  generalAllowanceFactorPercentage: '10',
  exposureFactorPercentage: 0,
  propertyFootprintInMetresSquared: '',
  propertyExternalPerimeterInMetres: '',
  buildTypeCode,
  externalAirTemperatureInDegreesCelsius,
  wallElements: [getDefaultUnitWallElementFormModel(externalAirTemperatureInDegreesCelsius)],
  roofElements: getDefaultUnitRoofElementFormModels(unit, externalAirTemperatureInDegreesCelsius),
  floorElements: getDefaultUnitFloorElementFormModels(unit),
});

export class UnitHeatLossDetailsFormModelValidator extends Validator<UnitHeatLossDetailsFormModel> {
  constructor(heatLossIsRequired: boolean) {
    super();

    this.ruleFor('generalAllowanceFactorPercentage')
      .must(beAnInteger)
      .must(beValidPercentage);

    this.ruleFor('generalAllowanceFactorPercentage')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .when(() => heatLossIsRequired);

    this.ruleFor('exposureFactorPercentage')
      .notNull()
      .withMessage(fieldRequiredValidationMessage);

    this.ruleFor('propertyFootprintInMetresSquared')
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1));

    this.ruleFor('propertyFootprintInMetresSquared')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .when(() => heatLossIsRequired);

    this.ruleFor('propertyExternalPerimeterInMetres')
      .must(beGreaterThanOrEqualTo(0))
      .must(haveDecimalPlaces(1));

    this.ruleFor('propertyExternalPerimeterInMetres')
      .notEmpty()
      .withMessage(fieldRequiredValidationMessage)
      .when(() => heatLossIsRequired);

    this.ruleForEach('wallElements').setValidator(() => new UnitWallElementFormModelValidator(heatLossIsRequired));

    this.ruleForEach('roofElements').setValidator(() => new UnitRoofElementFormModelValidator(heatLossIsRequired));

    this.ruleForEach('floorElements').setValidator(() => new UnitFloorElementFormModelValidator(heatLossIsRequired));
  }
}

export const getUnitDefaultExternalAirTemperature = (unitIndex: number) =>
  `units.${unitIndex}.heatLossDetails.externalAirTemperatureInDegreesCelsius`;
